import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { commonApi, holdingsApi, custodyFilesApi, SchemeCustodyDate } from '@/services/api';
import { UsdInrRate } from '@/types/client';
import { formatNumber } from '@/utils/formatters';
import NotificationsDropdown from '@/components/HeaderNotification';
import ScopeGate from '@/components/utils/ScopeGate'; // Import our ScopeGate!
import { useScope } from '@/hooks/useScope';
import { toast } from 'react-toastify';
import {
    READ_CLIENT_MASTER,
    WRITE_CLIENT_MASTER,
    READ_GENERAL_MASTERS,
    WRITE_GENERAL_MASTERS,
    ORDERS_MANAGEMENT,
    HOLDINGS_AND_DATA,
    FINANCIAL_MANAGEMENT,
    PORTFOLIO_TRANSACTIONS,
    FEES_AND_DISTRIBUTOR,
    PERFORMANCE,
    ANALYTICS,
    TRADE_SUGGESTIONS,
    KITE_CONNECT,
    USER_ROLE_MANAGEMENT,
    PORTFOLIO_QA,
    OPERATIONS_CHECKLIST,
    SOA_MANAGEMENT,
    ScopeCode
} from '@/constants/scopeCodes'; // Import scopes
import {
    Users, Target, Clipboard, Building, TrendingUp, FileText, Lock, DollarSign, Lightbulb,
    ChevronRight, LogOut, Calendar, ChevronDown, AlertCircle, ArrowUpDown, BarChart3, CalendarCheck, CandlestickChart, ClipboardList, ListChecks, ShoppingBasket, Wallet,
    PieChart, Calculator, Banknote, TrendingDown, Activity, Award, Briefcase, Menu, X, Upload,
    ActivitySquare, Plug, Shield, MessageCircle, SlidersHorizontal, ClipboardCheck, Landmark,
    Layers, Receipt
} from 'lucide-react';
import './Header.css';
import PropTypes from "prop-types";
import { Compare } from '@mui/icons-material';

/**
 * Colour of a dropdown badge. `fresh` / `stale` compare a date against a
 * reference; `neutral` is the reference value itself, which is not a verdict.
 */
type BadgeTone = 'fresh' | 'stale' | 'neutral';

interface DropdownItem {
    id: string;
    label: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    href?: string;
    badge?: string | number;
    badgeTone?: BadgeTone;
    badgeTitle?: string;
    disabled?: boolean;
    items?: DropdownItem[];
    divider?: boolean;
    scope?: ScopeCode | ScopeCode[];
}

interface DropdownProps {
    trigger: React.ReactNode;
    items: DropdownItem[];
    position?: 'left' | 'right';
    loading?: boolean;
    onOpen?: () => void;
    className?: string;
}

interface NavigationItem {
    href?: string;
    label: string;
    icon: React.ReactNode;
    items?: NavigationItem[];
    scope?: ScopeCode | ScopeCode[];
    key?: string;
}

// Latest holding date per FA source bundle (`Scheme.fa_source`) — `id` is the
// fa_source code string (e.g. "PMS"), not a numeric PK.
interface FaSourceHoldingDate {
    id: string;
    name: string;
    latest_holding_date: string | null;
}

// Map scopes to items (Parent items left blank if they contain mixed sub-scopes)
const baseNavigationItems: NavigationItem[] = [
    {
        label: 'Orders & Execution',
        key: 'orders',
        icon: <ClipboardList size={16} />,
        scope: [ORDERS_MANAGEMENT, TRADE_SUGGESTIONS],
        items: [
            { href: '/orders', label: 'Equity Orders', icon: <CandlestickChart size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/etf-orders', label: 'ETF Orders', icon: <ShoppingBasket size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/execution-board', label: 'Order Execution', icon: <ListChecks size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/order-allocations', label: 'Order Allocations', icon: <ArrowUpDown size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/block-trades', label: 'Block Trades', icon: <Layers size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/order-generation-failures', label: 'Order Generation Failures', icon: <AlertCircle size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/order-recommendation-list', label: 'Daily Orders & Recommendations', icon: <CalendarCheck size={16} />, scope: ORDERS_MANAGEMENT },
            { href: '/trade-suggestions', label: 'Trade Suggestions', icon: <Lightbulb size={16} />, scope: TRADE_SUGGESTIONS },
        ]
    },
    {
        label: 'Holdings',
        key: 'holdings',
        icon: <PieChart size={16} />,
        scope: [HOLDINGS_AND_DATA, ANALYTICS],
        items: [
            { href: '/holdings', label: 'Holdings', icon: <PieChart size={16} />, scope: HOLDINGS_AND_DATA },
            { href: '/scheme-wise-holdings', label: 'Scheme-Wise Holdings', icon: <BarChart3 size={16} />, scope: HOLDINGS_AND_DATA },
            { href: '/custody-holdings', label: 'Custody Holdings', icon: <Lock size={16} />, scope: HOLDINGS_AND_DATA },
            { href: '/bank-statements', label: 'Bank Statements', icon: <Landmark size={16} />, scope: HOLDINGS_AND_DATA },
            { href: '/risk-calculation', label: 'Risk Calculation', icon: <Calculator size={16} />, scope: ANALYTICS },
            { href: '/fa-holdings-process', label: 'FA Holdings Process', icon: <Upload size={16} />, scope: HOLDINGS_AND_DATA },
            { href: '/custody-process', label: 'Custody Process', icon: <Upload size={16} />, scope: HOLDINGS_AND_DATA },
            { href: '/contract-notes', label: 'Contract Notes', icon: <FileText size={16} />, scope: HOLDINGS_AND_DATA },
        ]
    },
    {
        label: 'Financial Management',
        icon: <DollarSign size={16} />,
        scope: [FINANCIAL_MANAGEMENT, PORTFOLIO_TRANSACTIONS, FEES_AND_DISTRIBUTOR],
        items: [
            { href: '/client-cash', label: 'Client Cash', icon: <Banknote size={16} />, scope: FINANCIAL_MANAGEMENT },
            { href: '/scheme-cash', label: 'Scheme Cash', icon: <Wallet size={16} />, scope: FINANCIAL_MANAGEMENT },
            { href: '/margin-calculation', label: 'Margin Calculation', icon: <Calculator size={16} />, scope: FINANCIAL_MANAGEMENT },
            { href: '/portfolio-transactions', label: 'Portfolio Transactions', icon: <TrendingDown size={16} />, scope: PORTFOLIO_TRANSACTIONS },
            { href: '/distributor-contribution', label: 'Distributor Contribution', icon: <Banknote size={16} />, scope: FEES_AND_DISTRIBUTOR },
            { href: '/cash-environment', label: 'Cash Environment', icon: <Banknote size={16} />, scope: FINANCIAL_MANAGEMENT },
            { href: '/sell/validation', label: 'Sell Validation', icon: <Banknote size={16} />, scope: FINANCIAL_MANAGEMENT },
            { href: '/management-fees', label: 'Management Fees', icon: <Calculator size={16} />, scope: FEES_AND_DISTRIBUTOR },
        ]
    },
    {
        label: 'Performance & Analytics',
        icon: <TrendingUp size={16} />,
        scope: [PERFORMANCE, ANALYTICS],
        items: [
            { href: '/portfolio-performance', label: 'Portfolio Performance', icon: <Award size={16} />, scope: PERFORMANCE },
            { href: '/stockwise-performance', label: 'Stockwise Performance', icon: <Award size={16} />, scope: PERFORMANCE },
            { href: '/clientwise-performance', label: 'Clientwise Performance', icon: <Award size={16} />, scope: PERFORMANCE },
            { href: '/portfolio-pe', label: 'Portfolio P/E', icon: <Calculator size={16} />, scope: ANALYTICS },
            { href: '/execution-analytics', label: 'Execution Analytics', icon: <Activity size={16} />, scope: ANALYTICS },
            { href: '/volume-monitor', label: 'Volume Monitor', icon: <BarChart3 size={15} />, scope: ANALYTICS },
            { href: '/broker-comparison', label: 'Broker Comparison', icon: <Compare fontSize="small" />, scope: ANALYTICS },
            { href: '/broker-wise-brokerage', label: 'Broker-Wise Brokerage', icon: <BarChart3 size={15} />, scope: ANALYTICS },
            { href: '/sector-holdings', label: 'Sector Weight Analysis', icon: <PieChart size={16} />, scope: ANALYTICS },
            { href: '/contribution-analysis', label: 'Contribution Analysis', icon: <BarChart3 size={16} />, scope: ANALYTICS },
        ]
    },
    {
        label: 'Masters',
        key: 'masters',
        icon: <Building size={16} />,
        scope: [READ_CLIENT_MASTER, WRITE_CLIENT_MASTER, READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS, ANALYTICS, USER_ROLE_MANAGEMENT],
        items: [
            { href: '/clients', label: 'Clients', icon: <Users size={16} />, scope: [READ_CLIENT_MASTER, WRITE_CLIENT_MASTER] },
            { href: '/companies', label: 'Companies', icon: <Building size={16} />, scope: [READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS] },
            { href: '/strategies', label: 'Strategies', icon: <Target size={16} />, scope: [READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS] },
            { href: '/schemes', label: 'Schemes', icon: <Clipboard size={16} />, scope: [READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS] },
            { href: '/masters', label: 'General Masters', icon: <Briefcase size={16} />, scope: [READ_GENERAL_MASTERS, WRITE_GENERAL_MASTERS] },
            { href: '/market-data-staleness', label: 'Market Data Staleness', icon: <ActivitySquare size={16} /> },
            { href: '/users', label: 'User Management', icon: <Users size={16} />, scope: USER_ROLE_MANAGEMENT },
            { href: '/roles-management', label: 'Roles Management', icon: <Shield size={16} />, scope: USER_ROLE_MANAGEMENT },
        ]
    },
    { href: '/soa', label: 'SOA', icon: <Receipt size={16} />, scope: SOA_MANAGEMENT },
    { href: '/kite-connect', label: 'Kite Connect', icon: <Plug size={16} />, scope: KITE_CONNECT },
    { href: '/qa', label: 'Portfolio Q&A', icon: <MessageCircle size={16} />, scope: PORTFOLIO_QA },
    { href: '/operations-checklist', label: 'Operations Checklist', icon: <ClipboardCheck size={16} />, scope: OPERATIONS_CHECKLIST },
];

/**
 * Date-only key, so a plain `YYYY-MM-DD` and a full timestamp for the same day
 * compare equal.
 */
const dateKey = (value?: string | null): string | null => (value ? value.slice(0, 10) : null);

/**
 * Every date in the Last Updated menu is judged against the last working day:
 * on that day it is current (green), anything else — including a missing date —
 * is behind (red). With no last working day to compare against there is no
 * verdict to give, so the badge stays untoned.
 */
const freshnessTone = (
    value: string | null | undefined,
    lastWorkingDay: string | null
): BadgeTone | undefined => {
    if (!lastWorkingDay) return undefined;
    return dateKey(value) === dateKey(lastWorkingDay) ? 'fresh' : 'stale';
};

const freshnessTitle = (tone: BadgeTone | undefined, lastWorkingDay: string | null): string | undefined => {
    if (!tone || !lastWorkingDay) return undefined;
    return tone === 'fresh'
        ? `Up to date with the last working day (${lastWorkingDay})`
        : `Behind the last working day (${lastWorkingDay})`;
};

const Dropdown: React.FC<DropdownProps> = React.memo(
    ({ trigger, items, position = 'right', loading = false, onOpen, className = '' }) => {
        const [isOpen, setIsOpen] = useState(false);
        const [openSubmenus, setOpenSubmenus] = useState<Set<string>>(new Set());
        const dropdownRef = useRef<HTMLDivElement>(null);
        const { pathname } = useLocation();

        const toggleDropdown = useCallback(() => {
            const newState = !isOpen;
            setIsOpen(newState);
            if (newState && onOpen) onOpen();
            if (!newState) setOpenSubmenus(new Set());
        }, [isOpen, onOpen]);

        const toggleSubmenu = useCallback((itemId: string) => {
            setOpenSubmenus(prev => {
                const newSet = new Set(prev);
                if (newSet.has(itemId)) newSet.delete(itemId);
                else newSet.add(itemId);
                return newSet;
            });
        }, []);

        const handleItemClick = useCallback(
            (item: DropdownItem, hasSubmenu: boolean) => {
                if (item.disabled) return;
                if (hasSubmenu) toggleSubmenu(item.id);
                else if (item.onClick) {
                    item.onClick();
                    setIsOpen(false);
                }
            },
            [toggleSubmenu]
        );

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                    setIsOpen(false);
                    setOpenSubmenus(new Set());
                }
            };
            if (isOpen) {
                document.addEventListener('mousedown', handleClickOutside);
                return () => document.removeEventListener('mousedown', handleClickOutside);
            }
        }, [isOpen]);

        const renderDropdownItem = useCallback(
            (item: DropdownItem, level = 0): React.ReactNode => {
                const hasSubmenu = !!(item.items && item.items.length);
                const isSubmenuOpen = openSubmenus.has(item.id);
                const isActive = !!item.href && item.href === pathname;

                if (item.divider) return <hr key={item.id} className="dropdown-divider" />;

                const itemContent = (
                    <div
                        className={`dropdown-item-container level-${level} ${item.disabled ? 'disabled' : ''} ${isActive ? 'active' : ''}`}
                        onClick={item.href ? undefined : () => handleItemClick(item, hasSubmenu)}
                    >
                        <div className="dropdown-item-content">
                            {item.icon && <div className="icon-wrapper">{item.icon}</div>}
                            <span className="item-label" title={item.label}>{item.label}</span>
                            {item.badge && (
                                <span
                                    className={`badge${item.badgeTone ? ` badge-${item.badgeTone}` : ''}`}
                                    title={item.badgeTitle}
                                >
                                    {item.badge}
                                </span>
                            )}
                        </div>
                        {hasSubmenu && (
                            <div className={`chevron-icon ${isSubmenuOpen ? 'open' : 'closed'}`}>
                                <ChevronRight size={14} />
                            </div>
                        )}
                    </div>
                );

                const itemNode = (
                    <React.Fragment key={item.id}>
                        {item.href ? (
                            <Link
                                to={item.href}
                                className={`styled-dropdown-link ${item.disabled ? 'disabled' : ''}`}
                                onClick={e => {
                                    if (item.disabled) e.preventDefault();
                                    else setIsOpen(false);
                                }}
                            >
                                {itemContent}
                            </Link>
                        ) : (
                            itemContent
                        )}

                        {hasSubmenu && isSubmenuOpen && (
                            <div className="submenu-wrapper">
                                {item.items?.map(sub => renderDropdownItem(sub, level + 1))}
                            </div>
                        )}
                    </React.Fragment>
                );

                // Hide item entirely if they lack the required scope
                return item.scope ? (
                    <ScopeGate key={item.id} scope={item.scope} fallback={null}>
                        {itemNode}
                    </ScopeGate>
                ) : itemNode;
            },
            [openSubmenus, handleItemClick, pathname]
        );

        return (
            <div ref={dropdownRef} className={`dropdown-wrapper ${className}`}>
                <div className="trigger-wrapper" onClick={toggleDropdown}>
                    {trigger}
                </div>
                {isOpen && (
                    <div className={`dropdown-menu position-${position}`}>
                        {loading ? (
                            <div className="loading-container">
                                <div className="spinner" />
                                <span>Loading...</span>
                            </div>
                        ) : (
                            items.map(item => renderDropdownItem(item))
                        )}
                    </div>
                )}
            </div>
        );
    }
);

Dropdown.propTypes = {
    trigger: PropTypes.node.isRequired,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.node,
            onClick: PropTypes.func,
            href: PropTypes.string,
            badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            badgeTone: PropTypes.oneOf(['fresh', 'stale', 'neutral']),
            badgeTitle: PropTypes.string,
            disabled: PropTypes.bool,
            items: PropTypes.array,
            divider: PropTypes.bool,
            scope: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
        }).isRequired
    ).isRequired,
    position: PropTypes.oneOf(['left', 'right']),
    loading: PropTypes.bool,
    onOpen: PropTypes.func,
    className: PropTypes.string
};
Dropdown.displayName = 'Dropdown';

const navItemToDropdownItem = (item: NavigationItem): DropdownItem => ({
    id: item.href || item.label,
    label: item.label,
    icon: item.icon,
    href: item.href,
    items: item.items?.map(navItemToDropdownItem),
    scope: item.scope, // Pass scope down!
});

const Header: React.FC = () => {
    const { user, logout, isSuperuser } = useAuth();
    // The statement endpoint is gated on holdings_and_data, so a user without it gets a
    // plain row rather than a click that can only fail.
    const canDownloadStatement = useScope(HOLDINGS_AND_DATA);
    const navigate = useNavigate();
    const location = useLocation();

    const navigationItems = useMemo<NavigationItem[]>(() => {
        return baseNavigationItems.map((item): NavigationItem => {
            // Inject Settings below Roles Management for superusers only
            if (isSuperuser && item.key === 'masters' && item.items) {
                const rolesIdx = item.items.findIndex(sub => sub.href === '/roles-management');
                const insertAt = rolesIdx >= 0 ? rolesIdx + 1 : item.items.length;
                return {
                    ...item,
                    items: [
                        ...item.items.slice(0, insertAt),
                        { href: '/settings', label: 'Settings', icon: <SlidersHorizontal size={16} /> },
                        ...item.items.slice(insertAt),
                    ],
                };
            }
            return item;
        });
    }, [isSuperuser]);

    const [faSourceDates, setFaSourceDates] = useState<FaSourceHoldingDate[]>([]);
    const [custodySchemeDates, setCustodySchemeDates] = useState<SchemeCustodyDate[]>([]);
    const [custodyLoadFailed, setCustodyLoadFailed] = useState(false);
    const [lastWorkingDay, setLastWorkingDay] = useState<string | null>(null);
    const [usdInrRate, setUsdInrRate] = useState<UsdInrRate | null>(null);
    const [lastUpdatedLoading, setLastUpdatedLoading] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [openMobileSubmenus, setOpenMobileSubmenus] = useState<Set<string>>(new Set());

    const navContainerRef = useRef<HTMLDivElement>(null);
    const measureItemsRef = useRef<(HTMLLIElement | null)[]>([]);
    const [overflowIndex, setOverflowIndex] = useState<number | null>(null);

    const fetchLastUpdated = useCallback(async () => {
        if (lastUpdatedLoading) return;
        setLastUpdatedLoading(true);
        // allSettled, not all: this dropdown is a freshness monitor, so one feed failing
        // must cost us that feed's rows and nothing else. Promise.all rejects as a unit
        // and would blank every section, which reads as "everything is fine" - the exact
        // failure this panel exists to catch.
        const [holdingsResult, custodyResult, commonResult, usdInrResult] = await Promise.allSettled([
            holdingsApi.getLatestDates({ source_wise: true }),
            custodyFilesApi.getSchemeWiseLatestDates(),
            commonApi.getLastWorkingDay(),
            commonApi.getLatestUsdInrRate()
        ]);

        if (holdingsResult.status === 'fulfilled') {
            setFaSourceDates(holdingsResult.value.source_wise_latest_holding_dates || []);
        } else {
            console.error('Error loading FA holdings dates:', holdingsResult.reason);
        }

        setCustodyLoadFailed(custodyResult.status === 'rejected');
        if (custodyResult.status === 'fulfilled') {
            setCustodySchemeDates(custodyResult.value.scheme_wise_latest_custody_dates || []);
        } else {
            console.error('Error loading custody holding dates:', custodyResult.reason);
        }

        if (commonResult.status === 'fulfilled') {
            setLastWorkingDay(commonResult.value.last_working_day);
        } else {
            console.error('Error loading last working day:', commonResult.reason);
        }

        setUsdInrRate(usdInrResult.status === 'fulfilled' ? usdInrResult.value : null);

        setLastUpdatedLoading(false);
    }, [lastUpdatedLoading]);

    // The account code and the holding date already identify the statement, and
    // custody-holding-files filters on exactly those two - so nothing about the file
    // has to travel in the freshness payload, and its signed URL is minted on click
    // rather than for every scheme on every open.
    const openCustodyStatement = useCallback(async (custodyCode: string, holdingDate: string) => {
        try {
            const { results } = await custodyFilesApi.getFiles({
                custody_code: custodyCode,
                custody_holding_date: holdingDate,
            });
            const file = results?.[0];
            if (!file?.file) {
                toast.error(`No custody statement stored for ${custodyCode} on ${holdingDate}.`);
                return;
            }
            window.open(file.file, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('Could not open custody statement:', error);
            toast.error('Could not open that custody statement.');
        }
    }, []);

    const handleLogout = useCallback(async () => {
        await logout();
        navigate('/login');
    }, [logout, navigate]);

    const toggleMobileMenu = useCallback(() => {
        setMobileMenuOpen(prev => !prev);
    }, []);

    const closeMobileMenu = useCallback(() => {
        setMobileMenuOpen(false);
        setOpenMobileSubmenus(new Set());
    }, []);

    const toggleMobileSubmenu = useCallback((label: string) => {
        setOpenMobileSubmenus(prev => {
            const newSet = new Set(prev);
            if (newSet.has(label)) newSet.delete(label);
            else newSet.add(label);
            return newSet;
        });
    }, []);

    const computeOverflow = useCallback(() => {
        const container = navContainerRef.current;
        if (!container) return;
        const containerWidth = container.offsetWidth - 8;
        if (!containerWidth) return;
        // Must track .nav-list's `gap` and the rendered width of the "More"
        // trigger in Header.css. Too large and the groups collapse into "More"
        // while the bar still has room.
        const GAP = 4;
        const MORE_BTN_WIDTH = 92;
        let used = 0;

        for (let i = 0; i < navigationItems.length; i++) {
            const el = measureItemsRef.current[i];
            // CRITICAL FIX: If ScopeGate made the item null, skip computing its width instead of breaking
            if (!el) continue;

            const w = el.offsetWidth + (i === 0 ? 0 : GAP);
            const reserve = i < navigationItems.length - 1 ? MORE_BTN_WIDTH + GAP : 0;
            if (used + w + reserve > containerWidth) {
                setOverflowIndex(i);
                return;
            }
            used += w;
        }
        setOverflowIndex(navigationItems.length);
    }, [navigationItems]);

    useEffect(() => {
        const el = navContainerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(computeOverflow);
        ro.observe(el);
        computeOverflow();
        return () => ro.disconnect();
    }, [computeOverflow]);

    /* ── Floating-bar scroll behaviour ───────────────────────────────────
       The pill floats over the page, so scrolled rows would otherwise run
       straight through it. `is-scrolled` firms up the frost and fades in the
       scrim behind the bar; `is-hidden` slides the dock away while the user
       scrolls down and brings it back the moment they scroll up.
       -------------------------------------------------------------------- */
    const [navScrolled, setNavScrolled] = useState(false);
    const [navHidden, setNavHidden] = useState(false);
    const lastScrollY = useRef(0);

    useEffect(() => {
        const HIDE_AFTER = 140; // px scrolled before the bar is allowed to hide
        const DELTA = 6;        // ignore trackpad jitter
        let frame = 0;

        const onScroll = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                const y = Math.max(window.scrollY, 0);
                const prev = lastScrollY.current;
                setNavScrolled(y > 6);
                if (Math.abs(y - prev) > DELTA) {
                    setNavHidden(y > prev && y > HIDE_AFTER);
                    lastScrollY.current = y;
                }
            });
        };

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) window.cancelAnimationFrame(frame);
        };
    }, []);

    // A hidden bar would strand the drawer's close button.
    useEffect(() => {
        if (mobileMenuOpen) setNavHidden(false);
    }, [mobileMenuOpen]);

    const lastUpdatedItems = useMemo<DropdownItem[]>(
        () => {
            const items: DropdownItem[] = [];

            if (lastWorkingDay) {
                items.push({
                    id: 'last-working-day',
                    label: 'Last Working Day',
                    badge: lastWorkingDay,
                    // The reference every other date below is measured against, not a verdict itself.
                    badgeTone: 'neutral',
                    icon: <Calendar size={14} />
                });
                items.push({ id: 'lwd-divider', label: '', divider: true });
            }

            if (usdInrRate?.rate_date) {
                const tone = freshnessTone(usdInrRate.rate_date, lastWorkingDay);
                items.push({
                    id: 'usd-inr',
                    label: usdInrRate.usd_inr != null ? `USD to INR (${formatNumber(usdInrRate.usd_inr, 2)})` : 'USD to INR',
                    badge: usdInrRate.rate_date,
                    badgeTone: tone,
                    badgeTitle: freshnessTitle(tone, lastWorkingDay),
                    icon: <DollarSign size={14} />
                });
                items.push({ id: 'usd-inr-divider', label: '', divider: true });
            }

            faSourceDates.forEach(source => {
                const tone = freshnessTone(source.latest_holding_date, lastWorkingDay);
                items.push({
                    id: source.id,
                    label: source.name,
                    badge: source.latest_holding_date || 'N/A',
                    badgeTone: tone,
                    badgeTitle: freshnessTitle(tone, lastWorkingDay),
                    disabled: !source.latest_holding_date
                });
            });

            if (custodyLoadFailed || custodySchemeDates.length > 0) {
                items.push({ id: 'custody-divider', label: '', divider: true });
                items.push({ id: 'custody-header', label: 'Custody Holdings', disabled: true });
                // A section that renders nothing when its fetch fails reads as "no custody
                // data expected", so say so instead.
                if (custodyLoadFailed) {
                    items.push({
                        id: 'custody-unavailable',
                        label: 'Could not load custody dates',
                        badge: 'Error',
                        badgeTone: 'stale',
                        icon: <FileText size={14} />,
                        disabled: true
                    });
                }
                custodySchemeDates.forEach(scheme => {
                    // A scheme with no custody data is the case worth seeing, so it gets a
                    // row too - 'stale' because behind the last working day is exactly what
                    // 'never arrived' is, and there is no fourth tone to distinguish them.
                    const tone = scheme.latest_holding_date
                        ? freshnessTone(scheme.latest_holding_date, lastWorkingDay)
                        : 'stale';
                    // The custody account is what an operator looks for; the scheme code
                    // says which scheme it landed against. An API-fed scheme has no
                    // statement, so the scheme code carries the row on its own - the
                    // full name only stands in where the code is unset, which would
                    // otherwise render a blank row. Long labels are ellipsized by
                    // .item-label, not wrapped.
                    const label = scheme.custody_code
                        ? `${scheme.custody_code}${scheme.code ? ` - ${scheme.code}` : ''}`
                        : scheme.code || scheme.name;
                    // An account fetched from the broker issues no statement, so that
                    // row stays plain - clicking it would have nothing to download.
                    const statement = scheme.custody_code
                        && scheme.latest_holding_date
                        && !scheme.is_api_based
                        ? { code: scheme.custody_code, date: scheme.latest_holding_date }
                        : null;
                    items.push({
                        id: `custody-scheme-${scheme.id}`,
                        label,
                        badge: scheme.latest_holding_date || 'Not received',
                        badgeTone: tone,
                        badgeTitle: scheme.latest_holding_date
                            ? freshnessTitle(tone, lastWorkingDay)
                            : 'No custody holdings have ever been loaded for this scheme',
                        icon: <FileText size={14} />,
                        disabled: !scheme.latest_holding_date,
                        onClick: statement && canDownloadStatement
                            ? () => openCustodyStatement(statement.code, statement.date)
                            : undefined
                    });
                });
            }

            return items;
        },
        [
            faSourceDates,
            custodySchemeDates,
            custodyLoadFailed,
            lastWorkingDay,
            usdInrRate,
            canDownloadStatement,
            openCustodyStatement
        ]
    );

    const userDropdownItems = useMemo<DropdownItem[]>(
        () => [
            { id: 'logout', label: 'Logout', icon: <LogOut size={16} />, onClick: handleLogout }
        ],
        [handleLogout]
    );

    const isActivePath = useCallback((path?: string) => (path ? location.pathname === path : false), [location.pathname]);

    // Recurses so a group folded into "More" (below) still reads as active.
    const isParentActive = useCallback(
        (items?: NavigationItem[]): boolean =>
            !!items?.some(item => isActivePath(item.href) || isParentActive(item.items)),
        [isActivePath]
    );

    const renderNavItemContent = useCallback(
        (item: NavigationItem, index: number) => {
            const hasSubmenu = !!(item.items && item.items.length);
            const isActive = isActivePath(item.href);
            const parentActive = hasSubmenu && isParentActive(item.items);

            if (hasSubmenu) {
                return (
                    <Dropdown
                        key={index}
                        trigger={
                            <button className={`nav-dropdown-trigger ${parentActive ? 'active' : ''}`}>
                                {item.icon}
                                <span>{item.label}</span>
                                <ChevronDown size={14} />
                            </button>
                        }
                        items={(item.items ?? []).map(navItemToDropdownItem)}
                        position="left"
                    />
                );
            }

            return (
                <Link key={index} to={item.href!} className={`nav-link ${isActive ? 'active' : ''}`}>
                    {item.icon}
                    <span>{item.label}</span>
                </Link>
            );
        },
        [isActivePath, isParentActive]
    );

    const renderMobileNavItem = useCallback(
        (item: NavigationItem, index: number) => {
            const hasSubmenu = !!(item.items && item.items.length);
            const isActive = isActivePath(item.href);
            const parentActive = hasSubmenu && isParentActive(item.items);
            const isSubmenuOpen = openMobileSubmenus.has(item.label);

            let liContent;

            if (hasSubmenu) {
                liContent = (
                    <li key={index} className="mobile-nav-item">
                        <button
                            className={`mobile-nav-button ${parentActive ? 'active' : ''}`}
                            onClick={() => toggleMobileSubmenu(item.label)}
                        >
                            <div className="mobile-nav-content">
                                {item.icon}
                                <span>{item.label}</span>
                            </div>
                            <ChevronDown size={16} className={`mobile-chevron ${isSubmenuOpen ? 'open' : ''}`} />
                        </button>
                        {isSubmenuOpen && (
                            <ul className="mobile-submenu">
                                {item.items?.map((sub) => {
                                    const key = sub.href ?? sub.label;
                                    const subLi = (
                                        <li key={key}>
                                            <Link
                                                to={sub.href!}
                                                className={`mobile-submenu-link ${isActivePath(sub.href) ? 'active' : ''}`}
                                                onClick={closeMobileMenu}
                                            >
                                                {sub.icon}
                                                <span>{sub.label}</span>
                                            </Link>
                                        </li>
                                    );
                                    return sub.scope ? (
                                        <ScopeGate key={key} scope={sub.scope} fallback={null}>{subLi}</ScopeGate>
                                    ) : subLi;
                                })}
                            </ul>
                        )}
                    </li>
                );
            } else {
                liContent = (
                    <li key={index} className="mobile-nav-item">
                        <Link
                            to={item.href!}
                            className={`mobile-nav-link ${isActive ? 'active' : ''}`}
                            onClick={closeMobileMenu}
                        >
                            <div className="mobile-nav-content">
                                {item.icon}
                                <span>{item.label}</span>
                            </div>
                        </Link>
                    </li>
                );
            }

            // Hide the mobile item entirely if the user lacks the scope
            return item.scope ? (
                <ScopeGate key={index} scope={item.scope} fallback={null}>{liContent}</ScopeGate>
            ) : liContent;
        },
        [isActivePath, isParentActive, openMobileSubmenus, toggleMobileSubmenu, closeMobileMenu]
    );

    const userInitial = useMemo(() => (user?.full_name || user?.email)?.[0]?.toUpperCase() || '?', [user]);
    const userName = useMemo(() => user?.full_name || user?.email || 'User', [user]);

    const dockClassName = [
        'header-dock',
        navScrolled ? 'is-scrolled' : '',
        navHidden && !mobileMenuOpen ? 'is-hidden' : ''
    ].filter(Boolean).join(' ');

    return (
        <>
            <div className={dockClassName}>
                <header className="header-container">
            <div className="header-left">
                <Link to="/" className="brand-link">
                    <img src="/oat-mark-light.png" alt="OAT — Operation Automation Toolkit" className="brand-logo" />
                </Link>
                <nav className="desktop-nav" ref={navContainerRef}>
                    <ul className="nav-list nav-measure-list" aria-hidden="true" inert>
                        {navigationItems.map((item, i) => {
                            const liContent = (
                                <li
                                    key={i}
                                    className="nav-list-item"
                                    ref={el => { measureItemsRef.current[i] = el; }}
                                >
                                    {renderNavItemContent(item, i)}
                                </li>
                            );
                            return item.scope ? (
                                <ScopeGate key={i} scope={item.scope} fallback={null}>{liContent}</ScopeGate>
                            ) : liContent;
                        })}
                    </ul>

                    <ul className="nav-list">
                        {overflowIndex !== null && navigationItems.map((item, idx) => {
                            if (idx >= overflowIndex) return null;
                            const liContent = (
                                <li key={idx} className="nav-list-item">
                                    {renderNavItemContent(item, idx)}
                                </li>
                            );
                            return item.scope ? (
                                <ScopeGate key={idx} scope={item.scope} fallback={null}>{liContent}</ScopeGate>
                            ) : liContent;
                        })}
                        {overflowIndex !== null && overflowIndex < navigationItems.length && (
                            <li className="nav-list-item">
                                <Dropdown
                                    trigger={
                                        <button className={`nav-dropdown-trigger ${isParentActive(navigationItems.slice(overflowIndex)) ? 'active' : ''}`}>
                                            <span>More</span>
                                            <ChevronDown size={14} />
                                        </button>
                                    }
                                    items={navigationItems.slice(overflowIndex).map(navItemToDropdownItem)}
                                    position="left"
                                />
                            </li>
                        )}
                    </ul>
                </nav>
            </div>

            {/* ... Rest of your component remains the same ... */}
            <div className="header-right">
                <div className="desktop-actions">
                    <Dropdown
                        trigger={
                            <button className="header-button">
                                <Calendar size={16} />
                                <span>Last Updated</span>
                            </button>
                        }
                        items={lastUpdatedItems}
                        loading={lastUpdatedLoading}
                        onOpen={fetchLastUpdated}
                    />
                    <NotificationsDropdown />
                    {user && (
                        <Dropdown
                            trigger={
                                <div className="user-trigger">
                                    <div className="user-avatar">{userInitial}</div>
                                    <div className="user-info">
                                        <span className="user-name">{userName}</span>
                                    </div>
                                    <ChevronDown size={14} />
                                </div>
                            }
                            items={userDropdownItems}
                        />
                    )}
                </div>
                <button className="mobile-menu-button" onClick={toggleMobileMenu} aria-label="Toggle menu">
                    {mobileMenuOpen ? <X size={24} className="mobile-menu-close-icon" /> : <Menu size={24} />}
                </button>
            </div>
                </header>
            </div>

            {mobileMenuOpen && (
                <>
                    <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>
                    <div className="mobile-menu">
                        <div className="mobile-menu-header">
                            <div className="user-section">
                                <div className="user-avatar">{userInitial}</div>
                                <span className="user-name">{userName}</span>
                                <button className="mobile-menu-close" onClick={closeMobileMenu}>
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        <nav className="mobile-nav">
                            <ul className="mobile-nav-list">
                                {navigationItems.map((item, index) => renderMobileNavItem(item, index))}
                            </ul>
                        </nav>
                        <div className="mobile-menu-footer">
                            <NotificationsDropdown />
                            <Dropdown
                                trigger={
                                    <button className="mobile-action-button last-updated-button">
                                        <Calendar size={18} />
                                        <span>Last Updated</span>
                                    </button>
                                }
                                items={lastUpdatedItems}
                                loading={lastUpdatedLoading}
                                onOpen={fetchLastUpdated}
                                position="left"
                                className="last-updated-dropdown"
                            />
                            <button className="mobile-action-button logout-button" onClick={() => {
                                handleLogout();
                                closeMobileMenu();
                            }}>
                                <LogOut size={18} />
                                <span>Logout</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default Header;